import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'

import { graphql_call as graphql_price_call } from '@repo/common/queries/helpers/pricing_query'
import { graphql_call } from '@repo/common/queries/helpers/server_query'

import { useGetTokenPrice } from '../fission_dex'
import { disableKeyWhenMissing } from '../helpers/utilities'
import {
  type AllFundSymbol,
  type FundSymbol,
  getTokenNames,
  isActiveFund,
  type SimulatedFundSymbol,
  type TokenName,
} from '../products/products_names'

import {
  query_fund_company_value,
  query_fund_daily_nav,
  query_list_funds,
  query_list_simulated_funds,
  query_simulated_fund_company_value,
  query_simulated_fund_daily_nav,
} from './funds_graphql'
import { query_get_latest_fund_data } from './funds_price_graphql'

export const keys = createQueryKeyStore({
  funds: {
    active: null,
    simulated: null,
    details: ({ symbol }: { symbol: string }) => ({
      queryKey: [{ symbol }],
      contextQueries: {
        price: null,
        company_value: null,
        daily_nav: null,
      },
    }),
  },
})

export type FundFindBy = {
  id?: RubyID
  symbol?: AllFundSymbol
}

const FundOrder = ['token-x', 'technology', 'fintech', 'consumer', 'blockchain']

export type AllFundType = TokenName & {
  symbol: AllFundSymbol
  id: RubyID
  price_per_share: number
  companies: {
    name: string
    cms_name: string
  }[]
}

export type SimulatedFundType = AllFundType & {
  symbol: SimulatedFundSymbol
}

export type ActiveFundType = AllFundType & {
  symbol: FundSymbol
  token_address: CryptoAddress
  minter_address: CryptoAddress
  minter_history: {
    address: CryptoAddress
    version: 'v1' | 'v2' | 'v3'
  }[]
}

export function useListSimulatedFunds() {
  const results = useQuery<SimulatedFundType[]>({
    queryKey: keys.funds.simulated.queryKey,
    queryFn: async () => {
      const data = await graphql_call({ query: query_list_simulated_funds })
      if (data == null) return []

      const funds = data.map((f) => {
        return {
          ...getTokenNames(f.symbol as AllFundSymbol),
          id: f.id,
          symbol: f.symbol,
          price_per_share: f.price_per_share,
          companies: f.companies,
        } as SimulatedFundType
      })

      return _.sortBy(funds, ({ name }) => {
        const index = FundOrder.indexOf(_.kebabCase(name))
        return index < 0 ? name : index.toString()
      })
    },
  })

  const data = results.data ?? []

  const fund_find_by = ({ id, symbol }: FundFindBy) =>
    data.find((f) => f.id == id || f.symbol === symbol?.toUpperCase())

  return {
    ...results,
    data,
    fund_find_by,
  }
}

export function useListActiveFunds() {
  const results = useQuery<ActiveFundType[]>({
    queryKey: keys.funds.active.queryKey,
    queryFn: async () => {
      const data = await graphql_call({ query: query_list_funds })
      if (data == null) return []

      const funds = data
        .filter((f) => isActiveFund(f.symbol as AllFundSymbol))
        .map((f) => {
          return {
            ...getTokenNames(f.symbol as AllFundSymbol),
            id: f.id,
            symbol: f.symbol,
            price_per_share: f.price_per_share,
            token_address: f.token_address,
            minter_address: f.minter?.address,
            minter_history: f.minter_history,
            companies: f.companies,
          } as ActiveFundType
        })

      return _.sortBy(funds, ({ name }) => {
        const index = FundOrder.indexOf(_.kebabCase(name))
        return index < 0 ? name : index.toString()
      })
    },
  })

  const data = results.data ?? []

  const fund_find_by = ({ id, symbol }: FundFindBy) =>
    data.find((f) => f.id == id || f.symbol === symbol?.toUpperCase())

  return {
    ...results,
    data,
    fund_find_by,
  }
}

export function useActiveFundInfo(args: { id?: RubyID; symbol?: FundSymbol }) {
  const { fund_find_by, ...others } = useListActiveFunds()

  return { ...others, data: fund_find_by(args) }
}

export function useSimulatedFundInfo(args: {
  id?: RubyID
  symbol?: SimulatedFundSymbol
}) {
  const { fund_find_by, ...others } = useListSimulatedFunds()

  return { ...others, data: fund_find_by(args) }
}

export function useAllFundInfo({ symbol }: { symbol?: AllFundSymbol }): {
  data: AllFundType | undefined
} {
  const { fund_find_by } = useListActiveFunds()
  const { fund_find_by: simulated_find_by } = useListSimulatedFunds()

  if (symbol == null) {
    return { data: undefined }
  } else if (isActiveFund(symbol)) {
    return { data: fund_find_by({ symbol }) }
  }

  return { data: simulated_find_by({ symbol }) }
}

export function useActiveFundPrice({ symbol }: { symbol?: FundSymbol }) {
  const { data: fund, isLoading: fund_isLoading } = useActiveFundInfo({
    symbol,
  })

  const price_result = useGetTokenPrice({ symbol: fund?.symbol })

  return {
    fund,
    price_result,
    fund_isLoading,
  }
}

export function useFundDailyNav({
  symbol,
}: {
  symbol: AllFundSymbol | undefined
}) {
  const { data } = useAllFundInfo({ symbol })
  const id = data?.id

  const enabled = id != null && symbol != null

  const results = useQuery({
    queryKey: keys.funds.details({ symbol: disableKeyWhenMissing(symbol) })._ctx
      .daily_nav.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          let query
          if (isActiveFund(symbol)) {
            query = query_fund_daily_nav
          } else {
            query = query_simulated_fund_daily_nav
          }

          const result = await graphql_call({
            query,
            variables: { id },
          })
          if (result == null) return []
          const dailies = result.daily_nav.map((n) => ({
            ...n,
            date: dayjs(n.date).valueOf(),
          }))
          return _.sortBy(dailies, 'date')
        },
  })
  return {
    ...results,
    data: results.data ?? [],
  }
}

// TODO replace with contract call
export function useFundLatestPriceData({
  symbol,
}: {
  symbol: FundSymbol | undefined
}) {
  const { data } = useAllFundInfo({ symbol })
  const id = data?.id

  const enabled = id != null && symbol != null

  return useQuery({
    queryKey: keys.funds.details({ symbol: disableKeyWhenMissing(symbol) })._ctx
      .price.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await graphql_price_call({
            query: query_get_latest_fund_data,
            variables: { id },
          })
          return result?.[0] ?? null
        },
  })
}

const empty_result = { companies: [], cash_balance: 0, funds_held_value: 0 }
export function useFundCompaniesValue({
  symbol,
}: {
  symbol: AllFundSymbol | undefined
}) {
  const { data } = useAllFundInfo({ symbol })
  const id = data?.id

  const enabled = id != null && symbol != null

  const results = useQuery({
    queryKey: keys.funds.details({ symbol: disableKeyWhenMissing(symbol) })._ctx
      .company_value.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          let query
          if (isActiveFund(symbol)) {
            query = query_fund_company_value
          } else {
            query = query_simulated_fund_company_value
          }

          const result = await graphql_call({
            query,
            variables: { id },
          })
          if (result == null) return empty_result
          const companies = result.companies ?? []
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const cash_balance = result.cash_balance ?? 0

          const funds_held_value = companies.reduce(
            (sum, c) => sum + c.value_held,
            0,
          )

          return { companies, cash_balance, funds_held_value }
        },
  })
  return {
    ...results,
    data: results.data ?? empty_result,
  }
}
